import { useState, useEffect } from "react";
import { Search, MapPin, Briefcase, Calendar, Send, CheckCircle2, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/config";

export default function OffersScreen() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [applying, setApplying] = useState(false);
  const [myAppliedIds, setMyAppliedIds] = useState(new Set());

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/offers?status=active`).catch(() => ({ data: [] })),
      axios.get(`${API}/applications/me`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([o, a]) => {
      setOffers(o.data || []);
      setMyAppliedIds(new Set((a.data || []).map((x) => x.offer_id)));
      setLoading(false);
    });
  }, []);

  const filtered = offers.filter((o) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      (o.title || "").toLowerCase().includes(s) ||
      (o.location || "").toLowerCase().includes(s) ||
      (o.description || "").toLowerCase().includes(s)
    );
  });

  const openOffer = (o) => setSelected(o);
  const closeOffer = () => setSelected(null);

  const apply = async () => {
    if (!selected) return;
    setApplying(true);
    try {
      await axios.post(`${API}/offers/${selected.offer_id}/apply`, {}, { withCredentials: true });
      setMyAppliedIds((prev) => new Set([...prev, selected.offer_id]));
      toast.success("Candidature envoyée");
      closeOffer();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setApplying(false); }
  };

  return (
    <div className="p-4 space-y-3" data-testid="offers-screen">
      <div className="flex items-center gap-2">
        <Search className="w-5 h-5 text-[#662483]" />
        <h1 className="text-lg font-bold text-[#3F3F3F]">Offres disponibles</h1>
        <Badge className="bg-[#F0E9F3] text-[#662483] rounded-none font-bold ml-1" data-testid="offers-count">{offers.length}</Badge>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un poste ou une ville..."
          className="rounded-none pl-9 text-sm h-10 bg-white"
          data-testid="offers-search"
        />
      </div>

      {loading && <p className="text-xs text-gray-500 italic">Chargement…</p>}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-500 border border-dashed border-gray-200 bg-white">
          <Briefcase className="w-8 h-8 mx-auto text-gray-300 mb-2" />
          Aucune offre correspondant.
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((o) => {
          const applied = myAppliedIds.has(o.offer_id);
          return (
            <button
              key={o.offer_id}
              onClick={() => openOffer(o)}
              className="w-full text-left bg-white border border-gray-200 p-3 hover:border-[#662483] transition-colors"
              data-testid={`offer-card-${o.offer_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-bold text-[#3F3F3F] flex-1 min-w-0">{o.title}</p>
                {applied && (
                  <Badge className="rounded-none text-[10px] bg-green-100 text-green-800 shrink-0" data-testid={`offer-applied-${o.offer_id}`}>
                    <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5" /> Postulé
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-1 flex-wrap">
                {o.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{o.location}</span>}
                {o.contract_type && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{o.contract_type}</span>}
                {o.start_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(o.start_date).toLocaleDateString("fr-FR")}</span>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && closeOffer()}>
        <DialogContent className="rounded-none p-0 max-w-full sm:max-w-lg h-full sm:h-auto sm:max-h-[85vh] flex flex-col overflow-hidden" data-testid="offer-dialog">
          <div className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white px-4 py-3 flex items-start justify-between gap-2">
            <DialogHeader className="flex-1">
              <DialogTitle className="text-white text-base font-bold text-left">{selected?.title}</DialogTitle>
              {selected?.location && (
                <p className="text-xs text-white/80 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{selected.location}</p>
              )}
            </DialogHeader>
            <button onClick={closeOffer} className="p-1.5 hover:bg-white/15" aria-label="Fermer" data-testid="offer-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          {selected && (
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div className="flex flex-wrap gap-2 text-xs">
                {selected.contract_type && <Badge className="rounded-none bg-[#F0E9F3] text-[#662483]">{selected.contract_type}</Badge>}
                {selected.start_date && <Badge className="rounded-none bg-gray-100 text-gray-700"><Calendar className="w-3 h-3 mr-1" />{new Date(selected.start_date).toLocaleDateString("fr-FR")}</Badge>}
                {selected.hourly_rate != null && <Badge className="rounded-none bg-gray-100 text-gray-700">{selected.hourly_rate} €/h</Badge>}
              </div>
              {selected.description && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1">Description</p>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{selected.description}</p>
                </div>
              )}
              {selected.profile && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1">Profil recherché</p>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{selected.profile}</p>
                </div>
              )}
              {selected.benefits && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1">Ce qu&apos;on vous propose</p>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{selected.benefits}</p>
                </div>
              )}
            </div>
          )}
          {selected && (
            <div className="border-t border-gray-200 p-3 bg-white">
              {myAppliedIds.has(selected.offer_id) ? (
                <Button disabled className="w-full rounded-none bg-green-600 text-white" data-testid="offer-already-applied">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Candidature envoyée
                </Button>
              ) : (
                <Button
                  onClick={apply}
                  disabled={applying}
                  className="w-full rounded-none bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold"
                  data-testid="offer-apply-btn"
                >
                  {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {applying ? "Envoi..." : "Postuler à cette offre"}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
